'use client';

import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import { cn } from '../../helpers/shadcnUtils.helper';
import * as Select from '@radix-ui/react-select';
import { ChevronDown, ChevronUp } from 'lucide-react';
export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  const [selectedMonth, setSelectedMonth] = React.useState(new Date());

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newMonth = new Date(selectedMonth);
    newMonth.setFullYear(Number(e.target.value));
    setSelectedMonth(newMonth);
  };

  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newMonth = new Date(selectedMonth);
    newMonth.setMonth(Number(e.target.value));
    setSelectedMonth(newMonth);
  };

  return (
    <div className={cn('p-3', className)}>
      {/* Calendar Component */}
      <DayPicker
        month={selectedMonth}
        onMonthChange={setSelectedMonth}
        showOutsideDays={showOutsideDays}
        classNames={{
          months:
            'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
          month: 'space-y-4 w-full',
          caption: 'flex justify-between pt-1 relative items-center gap-2',
          caption_label: 'flex items-center gap-2 text-sm font-medium',
          nav: ' flex items-center justify-center',
          nav_button: 'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
          nav_button_previous: 'left-2 mx-12',
          nav_button_next: 'absolute right-1',
          table: 'w-full border-collapse space-y-1',
          head_row: 'flex',
          head_cell: 'text-muted-foreground w-full font-normal text-[0.8rem]',
          row: 'flex w-full mt-2',
          cell: cn(
            'relative p-0 text-center w-full text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected].day-range-end)]:rounded-r-md',
            props.mode === 'range'
              ? '[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md'
              : '[&:has([aria-selected])]:rounded-md',
          ),
          day: 'h-8 w-8 p-0 font-normal aria-selected:opacity-100',
          day_range_start: 'day-range-start',
          day_range_end: 'day-range-end',
          day_selected:
            'bg-primary rounded-full text-white hover:bg-primary hover:text-white focus:bg-primary focus:text-white',
          day_today: 'bg-blue-95 text-blue-00 rounded-full',
          day_outside: 'day-outside opacity-50',
          day_disabled: 'text-muted-foreground opacity-50',
          day_range_middle:
            'aria-selected:bg-accent aria-selected:text-accent-foreground',
          day_hidden: 'invisible',
          ...classNames,
        }}
        components={{
          IconLeft: ({ className, ...props }) => (
            <ChevronLeft className={cn('h-4 w-4', className)} {...props} />
          ),
          IconRight: ({ className, ...props }) => (
            <ChevronRight className={cn('h-4 w-4', className)} {...props} />
          ),
          CaptionLabel: () => (
            <div className="flex items-center border rounded-lg">
              {/* Month Dropdown */}
              <Select.Root
                value={selectedMonth.getMonth().toString()}
                onValueChange={(value) =>
                  handleMonthChange({ target: { value } } as any)
                }
              >
                <Select.Trigger className="px-3 py-1 text-sm outline-none " data-testid="month-selector">
                  <Select.Value placeholder="Select month" />
                </Select.Trigger>
                <Select.Portal>
                  <Select.Content
                    position="popper"
                    className="bg-white z-50 border rounded-md shadow-md max-h-[250px] overflow-auto"
                  >
                    <Select.Viewport
                      className={cn(
                        'p-1 bg-white grid grid-cols-3',
                        'h-[var(--radix-select-trigger-height)] w-[150px] min-w-[150px] max-w-[150px]',
                      )}
                    >
                      {Array.from({ length: 12 }, (_, i) => (
                        <Select.Item
                          key={i}
                          value={i.toString()}
                          className="relative flex w-full cursor-pointer min-h-4 select-none items-center py-1 pl-1 pr-4 text-base outline-none focus:bg-blue-95 focus:text-blue-00 focus:rounded-md data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
                        >
                          <Select.ItemText>
                            {
                              [
                                'Jan.',
                                'Feb.',
                                'Mar.',
                                'Apr.',
                                'May.',
                                'June',
                                'July',
                                'Aug.',
                                'Sept.',
                                'Oct.',
                                'Nov.',
                                'Dec.',
                              ][i]
                            }
                          </Select.ItemText>
                        </Select.Item>
                      ))}
                    </Select.Viewport>
                  </Select.Content>
                </Select.Portal>
              </Select.Root>

              {/* Year Dropdown */}
              <Select.Root
                value={selectedMonth.getFullYear().toString()}
                onValueChange={(value) =>
                  handleYearChange({ target: { value } } as any)
                }
              >
                <Select.Trigger className="flex items-center gap-1 px-1 py-1 text-sm outline-none " data-testid="year-selector">
                  <Select.Value placeholder="Select year" />
                  <Select.Icon>
                    <ChevronDown className="w-4 h-4" />
                  </Select.Icon>
                </Select.Trigger>
                <Select.Portal>
                  <Select.Content
                    position="popper"
                    className="z-50 overflow-auto bg-white border rounded-md shadow-md"
                  >
                    <Select.Viewport
                      className={cn(
                        'p-1 bg-white grid grid-cols-3',
                        'h-[var(--radix-select-trigger-height)] max-h-[250px] w-[150px] min-w-[150px] max-w-[150px]',
                      )}
                    >
                      {Array.from({ length: 100 }, (_, i) => {
                        const year = 1970 + i;
                        return (
                          <Select.Item
                            key={year}
                            value={year.toString()}
                            className="py-4 relative flex w-full cursor-pointer min-h-4 select-none items-center my-1 pl-1 pr-4 text-base outline-none focus:bg-blue-95 focus:text-blue-00 focus:rounded-md data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
                          >
                            <Select.ItemText>{year}</Select.ItemText>
                          </Select.Item>
                        );
                      })}
                    </Select.Viewport>
                  </Select.Content>
                </Select.Portal>
              </Select.Root>
            </div>
          ),
        }}
        {...props}
      />
    </div>
  );
}

Calendar.displayName = 'Calendar';

export { Calendar };
